import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import Modal from './Modal';
import './ShareManager.css';

function formatDate(iso) {
  if (!iso) return 'Never';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

function isExpired(expiresAt) {
  if (!expiresAt) return false;
  return new Date() > new Date(expiresAt);
}

export default function ShareManager({ eventId, eventName, onClose }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newExpiry, setNewExpiry] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);
  function closeModal() { setModal(null); }

  const fetchLinks = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/share?eventId=${eventId}`);
      setLinks(data.links);
    } catch {
      setError('Failed to load share links');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => { fetchLinks(); }, [fetchLinks]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  async function handleCreate(e) {
    e.preventDefault();
    setCreating(true);
    setError('');
    try {
      const { data } = await api.post('/api/share', {
        eventId,
        label: newLabel.trim() || undefined,
        expiresAt: newExpiry || undefined,
      });
      setLinks((prev) => [data.link, ...prev]);
      setNewLabel('');
      setNewExpiry('');
    } catch {
      setError('Failed to create share link');
    } finally {
      setCreating(false);
    }
  }

  async function handleToggle(link) {
    try {
      const { data } = await api.patch(`/api/share/${link.id}`, { isActive: !link.isActive });
      setLinks((prev) => prev.map((l) => (l.id === link.id ? data.link : l)));
    } catch {
      setError('Failed to update link');
    }
  }

  function handleDelete(linkId) {
    setModal({
      onConfirm: async () => {
        closeModal();
        try {
          await api.delete(`/api/share/${linkId}`);
          setLinks((prev) => prev.filter((l) => l.id !== linkId));
        } catch {
          setError('Failed to delete link');
        }
      },
    });
  }

  function buildShareUrl(token) {
    return `${window.location.origin}/s/${token}`;
  }

  function handleCopy(link) {
    navigator.clipboard.writeText(buildShareUrl(link.token));
    setCopiedId(link.id);
    setTimeout(() => setCopiedId(null), 2000);
  }

  return (
    <>
    <div className="sm-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="sm-panel">
        <div className="sm-header">
          <div>
            <h2>Share links</h2>
            {eventName && <p className="sm-event-name">Event: {eventName}</p>}
          </div>
          <button className="sm-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <p className="sm-desc">
          Anyone with a share link can browse and view the photos &amp; videos in this event — but cannot upload or delete files.
        </p>

        {error && <div className="banner banner-error" style={{ margin: '0 1.5rem' }}>{error}</div>}

        <form className="sm-create" onSubmit={handleCreate}>
          <input
            className="sm-input"
            type="text"
            placeholder="Link label (optional)"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            maxLength={80}
          />
          <label className="sm-expiry-label">
            Expires
            <input
              className="sm-input"
              type="date"
              value={newExpiry}
              min={new Date().toISOString().split('T')[0]}
              onChange={(e) => setNewExpiry(e.target.value)}
            />
          </label>
          <button type="submit" className="btn btn-primary sm-create-btn" disabled={creating}>
            {creating ? <span className="spinner" /> : '+ Create link'}
          </button>
        </form>

        <div className="sm-list">
          {loading ? (
            <div className="sm-empty"><div className="spinner" /></div>
          ) : links.length === 0 ? (
            <div className="sm-empty">No share links yet. Create one above.</div>
          ) : (
            links.map((link) => {
              const expired = isExpired(link.expiresAt);
              const inactive = !link.isActive;
              const invalid = expired || inactive;
              return (
                <div key={link.id} className={`sm-link ${invalid ? 'sm-link--invalid' : ''}`}>
                  <div className="sm-link-top">
                    <span className="sm-link-label">
                      {link.label || 'Untitled link'}
                      {expired && <span className="sm-badge sm-badge--expired">Expired</span>}
                      {!expired && inactive && <span className="sm-badge sm-badge--off">Off</span>}
                    </span>
                    <div className="sm-link-actions">
                      <button className="btn btn-ghost btn-xs" onClick={() => handleToggle(link)}>
                        {link.isActive ? 'Disable' : 'Enable'}
                      </button>
                      <button className="btn btn-danger btn-xs" onClick={() => handleDelete(link.id)}>
                        Delete
                      </button>
                    </div>
                  </div>
                  <div className="sm-link-url">
                    <span className="sm-url-text">{buildShareUrl(link.token)}</span>
                    <button
                      className={`btn btn-ghost btn-xs sm-copy ${copiedId === link.id ? 'sm-copy--done' : ''}`}
                      onClick={() => handleCopy(link)}
                    >
                      {copiedId === link.id ? '✓ Copied' : 'Copy'}
                    </button>
                  </div>
                  <div className="sm-link-meta">
                    Created {formatDate(link.createdAt)} · Expires: {formatDate(link.expiresAt)}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>

      {modal && (
        <Modal
          title="Delete share link"
          message="Anyone using this link will lose access immediately."
          type="confirm"
          danger
          confirmLabel="Delete link"
          onConfirm={modal.onConfirm}
          onCancel={closeModal}
        />
      )}
    </>
  );
}
